import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, FileJson, AlertCircle, CheckCircle } from 'lucide-react';

const ImportModal = ({ isOpen, onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [conflictResolution, setConflictResolution] = useState('skip');
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (selectedFile) => {
    if (selectedFile && selectedFile.type === 'application/json') {
      setFile(selectedFile);
      
      // Read and preview file
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          setPreview(data);
          setResult(null);
        } catch (error) {
          alert('Fichier JSON invalide');
          setFile(null);
          setPreview(null);
        }
      };
      reader.readAsText(selectedFile);
    } else {
      alert('Veuillez sélectionner un fichier JSON valide');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleImport = async () => {
    if (!file || !preview) {
      alert('Aucun fichier sélectionné');
      return;
    }

    setImporting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts-library/import?conflict_resolution=${conflictResolution}`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de l\'import');
      }

      const data = await response.json();
      
      if (data.success) {
        setResult({
          success: true,
          imported: data.imported,
          skipped: data.skipped,
          errors: data.errors
        });
        
        // Call callback after 2 seconds
        setTimeout(() => {
          onImportComplete && onImportComplete();
          handleClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error importing prompts:', error);
      setResult({
        success: false,
        message: error.message
      });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setConflictResolution('skip');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="backdrop-blur-xl bg-slate-900/95 border border-white/10 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Upload className="text-cyan-400" size={28} />
              Importer des Prompts
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="text-slate-400" size={24} />
            </button>
          </div>

          {/* Upload Area */}
          {!file && (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer ${
                isDragging
                  ? 'border-cyan-500 bg-cyan-500/10'
                  : 'border-white/20 hover:border-cyan-500/50 hover:bg-white/5'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <FileJson className={`mx-auto mb-4 ${isDragging ? 'text-cyan-400' : 'text-slate-500'}`} size={64} />
              <p className="text-lg text-white mb-2">
                {isDragging ? 'Déposez le fichier ici' : 'Glissez-déposez votre fichier JSON'}
              </p>
              <p className="text-sm text-slate-400 mb-4">ou</p>
              <button
                type="button"
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
              >
                Parcourir les fichiers
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
              />
            </div>
          )}

          {/* Preview */}
          {file && preview && !result && (
            <div className="space-y-6">
              {/* File Info */}
              <div className="bg-slate-800/50 border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <FileJson className="text-cyan-400" size={32} />
                  <div className="flex-1">
                    <p className="text-white font-semibold">{file.name}</p>
                    <p className="text-sm text-slate-400">
                      {preview.prompt_count || preview.prompts?.length || 0} prompts • {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setFile(null);
                      setPreview(null);
                    }}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="text-slate-400" size={20} />
                  </button>
                </div>
              </div>

              {/* Preview Prompts */}
              {preview.prompts && preview.prompts.length > 0 && (
                <div className="bg-slate-800/30 border border-white/10 rounded-xl p-4">
                  <p className="text-sm font-medium text-slate-300 mb-3">
                    Aperçu des prompts ({preview.prompts.length})
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {preview.prompts.slice(0, 5).map((prompt, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-slate-400">
                        <CheckCircle size={14} className="text-green-400" />
                        {prompt.name}
                        {prompt.category && (
                          <span className="text-xs px-2 py-0.5 bg-indigo-500/20 text-indigo-400 rounded-md">
                            {prompt.category}
                          </span>
                        )}
                      </div>
                    ))}
                    {preview.prompts.length > 5 && (
                      <p className="text-xs text-slate-500 italic">
                        +{preview.prompts.length - 5} autres prompts...
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Conflict Resolution */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  Résolution des conflits
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 bg-slate-800/30 border border-white/10 rounded-lg cursor-pointer hover:bg-slate-800/50 transition-all">
                    <input
                      type="radio"
                      name="conflict"
                      value="skip"
                      checked={conflictResolution === 'skip'}
                      onChange={(e) => setConflictResolution(e.target.value)}
                      className="w-4 h-4 text-cyan-500"
                    />
                    <div>
                      <p className="text-white font-medium">Ignorer les doublons</p>
                      <p className="text-xs text-slate-400">Les prompts existants ne seront pas modifiés</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-slate-800/30 border border-white/10 rounded-lg cursor-pointer hover:bg-slate-800/50 transition-all">
                    <input
                      type="radio"
                      name="conflict"
                      value="rename"
                      checked={conflictResolution === 'rename'}
                      onChange={(e) => setConflictResolution(e.target.value)}
                      className="w-4 h-4 text-cyan-500"
                    />
                    <div>
                      <p className="text-white font-medium">Renommer les doublons</p>
                      <p className="text-xs text-slate-400">Ajouter un suffixe (1), (2), etc.</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-slate-800/30 border border-white/10 rounded-lg cursor-pointer hover:bg-slate-800/50 transition-all">
                    <input
                      type="radio"
                      name="conflict"
                      value="overwrite"
                      checked={conflictResolution === 'overwrite'}
                      onChange={(e) => setConflictResolution(e.target.value)}
                      className="w-4 h-4 text-cyan-500"
                    />
                    <div>
                      <p className="text-white font-medium">Écraser les doublons</p>
                      <p className="text-xs text-slate-400">⚠️ Remplacer les prompts existants</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setFile(null);
                    setPreview(null);
                  }}
                  className="flex-1 px-6 py-3 bg-slate-800 text-slate-300 rounded-xl border border-white/10 hover:bg-slate-700 transition-all font-semibold"
                  disabled={importing}
                >
                  Annuler
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? 'Import en cours...' : 'Importer'}
                </button>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className={`p-6 rounded-xl border ${
              result.success 
                ? 'bg-green-500/10 border-green-500/30' 
                : 'bg-red-500/10 border-red-500/30'
            }`}>
              {result.success ? (
                <div className="text-center">
                  <CheckCircle className="mx-auto text-green-400 mb-4" size={48} />
                  <h3 className="text-xl font-bold text-white mb-2">Import réussi !</h3>
                  <p className="text-green-400">
                    {result.imported} prompts importés
                    {result.skipped > 0 && `, ${result.skipped} ignorés`}
                  </p>
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-xs text-red-400 mb-1">Erreurs :</p>
                      {result.errors.map((error, index) => (
                        <p key={index} className="text-xs text-slate-400">{error}</p>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <AlertCircle className="mx-auto text-red-400 mb-4" size={48} />
                  <h3 className="text-xl font-bold text-white mb-2">Erreur d'import</h3>
                  <p className="text-red-400">{result.message}</p>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImportModal;
